<template>
  <div class="visitMapShell">
    <div ref="map" class="visitMapCanvas"></div>
    <button
      v-if="map"
      type="button"
      class="mapLayerToggle"
      :aria-pressed="isSatellite"
      :title="isSatellite ? 'Exibir mapa padrão' : 'Exibir mapa por satélite'"
      @click="toggleMapLayer"
    >
      {{ isSatellite ? "Mapa" : "Satélite" }}
    </button>
    <div v-if="isLoading" class="visitMapFeedback">
      <b-spinner small variant="success" />
      <span>Carregando mapa...</span>
    </div>
    <div v-if="errorMessage" class="visitMapError">
      <b-icon-exclamation-triangle-fill />
      <span>{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
const MAPS_SCRIPT_ID = "winfit-google-maps-sdk";
let mapsLoaderPromise = null;

function loadGoogleMaps() {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (mapsLoaderPromise) return mapsLoaderPromise;

  mapsLoaderPromise = new Promise((resolve, reject) => {
    const apiKey = process.env.VUE_APP_KEY_API_GOOGLE_MAPS;
    if (!apiKey) {
      reject(new Error("Chave do Google Maps não configurada."));
      return;
    }

    const existingScript =
      document.getElementById(MAPS_SCRIPT_ID) ||
      document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      existingScript.addEventListener(
        "load",
        () => resolve(window.google.maps),
        { once: true }
      );
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Não foi possível carregar o Google Maps.")),
        { once: true }
      );
      return;
    }

    const geolocationBaseUrl = process.env.VUE_APP_KEY_BASE_URL_GEOLOCATION;
    const googleOrigin = geolocationBaseUrl
      ? new URL(geolocationBaseUrl).origin
      : "https://maps.googleapis.com";
    const script = document.createElement("script");
    script.id = MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `${googleOrigin}/maps/api/js?key=${encodeURIComponent(
      apiKey
    )}&language=pt-BR`;
    script.onload = () => resolve(window.google.maps);
    script.onerror = () =>
      reject(new Error("Não foi possível carregar o Google Maps."));
    document.head.appendChild(script);
  }).catch((error) => {
    mapsLoaderPromise = null;
    throw error;
  });

  return mapsLoaderPromise;
}

export default {
  name: "InspectionVisitMap",
  props: {
    locations: { type: Array, default: () => [] },
  },
  data() {
    return {
      map: null,
      markers: [],
      infoWindow: null,
      isLoading: true,
      errorMessage: "",
      isSatellite: false,
    };
  },
  watch: {
    locations: {
      deep: true,
      handler() {
        if (this.map) this.renderMarkers();
      },
    },
  },
  mounted() {
    this.initializeMap();
  },
  beforeDestroy() {
    this.clearMarkers();
    if (this.infoWindow) this.infoWindow.close();
    this.infoWindow = null;
    this.map = null;
  },
  methods: {
    async initializeMap() {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        await loadGoogleMaps();
        if (!this.$refs.map || this._isBeingDestroyed) return;

        const firstLocation = this.locations[0];
        this.map = new window.google.maps.Map(this.$refs.map, {
          center: {
            lat: Number(firstLocation.latitude),
            lng: Number(firstLocation.longitude),
          },
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          clickableIcons: false,
        });
        this.infoWindow = new window.google.maps.InfoWindow();
        this.renderMarkers();
      } catch (error) {
        this.errorMessage =
          error.message || "Não foi possível inicializar o mapa.";
      } finally {
        this.isLoading = false;
      }
    },
    renderMarkers() {
      if (!this.map || !window.google?.maps) return;
      this.clearMarkers();

      const groupedLocations = this.locations.reduce((groups, location) => {
        const key = `${Number(location.latitude).toFixed(7)}:${Number(
          location.longitude
        ).toFixed(7)}`;
        const group = groups.get(key) || [];
        group.push(location);
        groups.set(key, group);
        return groups;
      }, new Map());
      const bounds = new window.google.maps.LatLngBounds();

      groupedLocations.forEach((visits) => {
        const first = visits[0];
        const position = {
          lat: Number(first.latitude),
          lng: Number(first.longitude),
        };
        const marker = new window.google.maps.Marker({
          map: this.map,
          position,
          title:
            visits.length > 1
              ? `${visits.length} visitas neste local`
              : `${first.customer} · ${first.property}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: this.markerColor(visits),
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
            scale: visits.length > 1 ? 9 : 7,
          },
          label:
            visits.length > 1
              ? {
                  text: String(visits.length),
                  color: "#ffffff",
                  fontSize: "10px",
                  fontWeight: "700",
                }
              : undefined,
        });
        marker.addListener("click", () => {
          this.infoWindow.setContent(this.infoContent(visits));
          this.infoWindow.open(this.map, marker);
        });
        this.markers.push(marker);
        bounds.extend(position);
      });

      if (!this.markers.length) return;
      if (this.markers.length === 1) {
        this.map.setCenter(bounds.getCenter());
        this.map.setZoom(14);
        return;
      }
      this.map.fitBounds(bounds, 42);
      window.google.maps.event.addListenerOnce(this.map, "bounds_changed", () => {
        if (this.map.getZoom() > 16) this.map.setZoom(16);
      });
    },
    clearMarkers() {
      this.markers.forEach((marker) => {
        window.google?.maps?.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
      this.markers = [];
    },
    toggleMapLayer() {
      if (!this.map) return;

      this.isSatellite = !this.isSatellite;
      this.map.setMapTypeId(
        this.isSatellite
          ? window.google.maps.MapTypeId.HYBRID
          : window.google.maps.MapTypeId.ROADMAP
      );
    },
    markerColor(visits) {
      const statuses = new Set(visits.map((visit) => visit.status));
      if (statuses.size > 1) return "#6957b8";
      if (statuses.has("Concluído")) return "#14a363";
      if (statuses.has("Em andamento")) return "#2f72bd";
      return "#e7ae25";
    },
    infoContent(visits) {
      const items = visits
        .map(
          (visit) => `
            <div style="padding:5px 0;border-bottom:1px solid #edf1ef">
              <strong>${this.escapeHtml(visit.customer)}</strong><br>
              <span>${this.escapeHtml(visit.property)}</span><br>
              <small>${this.escapeHtml(visit.source)} · ${this.escapeHtml(
            visit.status
          )}</small>
            </div>`
        )
        .join("");
      return `<div style="min-width:190px;max-width:270px;font:12px Poppins,Arial,sans-serif">
        <b>${visits.length > 1 ? `${visits.length} visitas` : "Visita técnica"}</b>
        ${items}
      </div>`;
    },
    escapeHtml(value) {
      const element = document.createElement("div");
      element.textContent = String(value || "Não informado");
      return element.innerHTML;
    },
  },
};
</script>

<style scoped>
.visitMapShell {
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
  border: 1px solid #dfe7e4;
  border-radius: 7px;
  background: #eef2f1;
}
.visitMapCanvas {
  width: 100%;
  height: 100%;
}
.mapLayerToggle {
  position: absolute;
  z-index: 1;
  top: 10px;
  left: 10px;
  padding: 7px 12px;
  border: 1px solid #d7e2df;
  border-radius: 4px;
  background: #fff;
  color: #006256;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}
.mapLayerToggle:hover,
.mapLayerToggle:focus {
  border-color: #006256;
  outline: none;
}
.visitMapFeedback,
.visitMapError {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  background: rgba(246, 250, 248, 0.92);
  color: #53635e;
  font-size: 12px;
  text-align: center;
}
.visitMapError {
  color: #a13d36;
}
</style>
